import { useEffect } from 'react';
import { AudioEngine } from '@engine/audio';
import { useAudioStore, useSynthStore } from '@store/index';

/**
 * Module-level singleton — survives React StrictMode's double-mount in dev
 * and remains the same reference across HMR. iOS Safari requires that the
 * AudioContext be CREATED and RESUMED inside the same synchronous call
 * stack as a user gesture, so disposing on unmount breaks audio in dev.
 */
const engineRef: { current: AudioEngine | null } = { current: null };

export function useAudioEngine() {
  const setInitialized = useAudioStore((s) => s.setInitialized);
  const setLatency = useAudioStore((s) => s.setLatency);
  const volume = useAudioStore((s) => s.volume);

  if (!engineRef.current) engineRef.current = new AudioEngine({ volume });

  useEffect(() => {
    engineRef.current?.setVolume(volume);
  }, [volume]);

  // Feed the engine from the synth store: active slot's preset. The
  // store owns the editable state; the engine just consumes the
  // current snapshot on every change. Gated so we only push when the
  // slot index OR the slot's content changes.
  useEffect(() => {
    const push = () => {
      const s = useSynthStore.getState();
      const slot = s.slots[s.activeSlotIndex];
      if (slot) engineRef.current?.setTimbre(slot);
    };
    push();
    const unsub = useSynthStore.subscribe((s, prev) => {
      if (s.activeSlotIndex !== prev.activeSlotIndex) return push();
      if (s.slots[s.activeSlotIndex] !== prev.slots[prev.activeSlotIndex]) push();
    });
    return unsub;
  }, []);

  /**
   * SYNCHRONOUS. Must be called inside a user gesture handler
   * (pointerdown / click / keydown / touchstart).
   */
  const ensureStarted = (): AudioEngine => {
    const engine = engineRef.current!;
    if (!engine.isReady()) {
      engine.initialize();
      setInitialized(true);
      setLatency(engine.outputLatencyMs);
    } else {
      engine.tryResume();
    }
    return engine;
  };

  return { engineRef, ensureStarted };
}
